/**
 * Resolución de códigos de idioma con variantes regionales (HRS-2934).
 *
 * El CMS separa pt-BR y es-AR de sus idiomas base como idiomas distintos, así que aquí ya no
 * llegan solo códigos de dos letras. Todo lo que indexe por idioma (textos del
 * CMS, frases de voz, layouts de teclado) tiene que resolver el código contra lo
 * que realmente hay, en vez de acceder por clave exacta y quedarse a cero.
 *
 * Criterio, el mismo que aplica el CMS:
 *   1. Código exacto (normalizando casing y guion bajo: `pt_br` → `pt-BR`).
 *   2. Idioma base de la variante (`pt-BR` → `pt`).
 *   3. Una variante del idioma pedido (`pt` → `pt-BR`), útil cuando el catálogo
 *      solo trae variantes.
 *
 * Las variantes hermanas NO se cruzan: nunca se sirve `pt-BR` desde otra variante hermana,
 * porque el que lee es un turista y la diferencia se nota.
 *
 * Funciones PURAS, testables en aislado — ver language.util.spec.ts.
 */

/** Normaliza a la forma del CMS: `pt_br`, `PT-BR`, ` pt-br ` → `pt-BR`. */
export function normalizeLanguageCode(code: string | null | undefined): string {
  if (!code)
    return '';

  const parts = String(code).trim().replace(/_/g, '-').split('-').filter(Boolean);
  if (!parts.length)
    return '';

  const language = parts[0].toLowerCase();
  return parts.length > 1 ? `${language}-${parts[1].toUpperCase()}` : language;
}

/** Idioma base de una variante regional: `pt-BR` → `pt`. Sin región, el propio código. */
export function baseLanguageCode(code: string | null | undefined): string {
  return normalizeLanguageCode(code).split('-')[0] ?? '';
}

/**
 * Devuelve, de entre `available`, el código que debe usarse para `code`, o
 * `null` si no hay ninguno servible.
 */
export function resolveLanguageCode(
  code: string | null | undefined,
  available: readonly string[] | null | undefined
): string | null {
  const wanted = normalizeLanguageCode(code);
  const options = (available ?? []).filter(Boolean);
  if (!wanted || !options.length)
    return null;

  const byNormalized = new Map<string, string>();
  for (const option of options) {
    const normalized = normalizeLanguageCode(option);
    if (normalized && !byNormalized.has(normalized))
      byNormalized.set(normalized, option);
  }

  // 1. Exacto
  const exact = byNormalized.get(wanted);
  if (exact !== undefined)
    return exact;

  const base = baseLanguageCode(wanted);

  // 2. Idioma base de la variante pedida
  const baseMatch = byNormalized.get(base);
  if (baseMatch !== undefined)
    return baseMatch;

  // 3. Una variante del idioma pedido, solo si se pidió el idioma base
  if (wanted === base) {
    for (const [normalized, original] of byNormalized) {
      if (normalized.split('-')[0] === base)
        return original;
    }
  }

  return null;
}

/**
 * Valor de un diccionario indexado por idioma, aplicando el mismo criterio.
 * Devuelve `undefined` cuando no hay nada servible, para que el llamador decida
 * si cae a un valor por defecto o muestra vacío.
 */
export function pickByLanguage<T>(
  source: Record<string, T> | null | undefined,
  code: string | null | undefined
): T | undefined {
  if (!source)
    return undefined;

  const resolved = resolveLanguageCode(code, Object.keys(source));
  return resolved === null ? undefined : source[resolved];
}
